import { create } from "zustand";

export const ALLOWED_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
];

export interface AttachedDesign {
  id: string;
  name: string;
  description: string;
  promptText: string;
}

interface PromptInputActions {
  setPrompt: (prompt: string | null) => void;
  setAttachedImage: (image: File | null) => void;
  setAttachedDesign: (design: AttachedDesign | null) => void;
  addFileReference: (filePath: string) => void;
  removeFileReference: (filePath: string) => void;
  setIsSubmitting: (isSubmitting: boolean) => void;
  isLengthExceeded: () => boolean;
  saveInLocalStorage: () => void;
  loadFromLocalStorage: () => void;
  clear: () => void;
}

interface PromptInputState {
  maxPromptLength: number;
  prompt: string | null;
  attachedImage: File | null;
  attachedDesign: AttachedDesign | null;
  referencedFiles: string[];
  isSubmitting: boolean;

  actions: PromptInputActions;
}

const usePromptInputStore = create<PromptInputState>((set, get) => ({
  maxPromptLength: 50_000,
  prompt: null,
  attachedImage: null,
  attachedDesign: null,
  referencedFiles: [],
  isSubmitting: false,

  actions: {
    setPrompt: (prompt) => set({ prompt }),

    setAttachedImage: (attachedImage) => set({ attachedImage }),

    setAttachedDesign: (attachedDesign) => set({ attachedDesign }),

    addFileReference: (filePath: string) => {
      const current = get().referencedFiles;
      // Prevent duplicates
      if (current.includes(filePath)) return;
      // Enforce 10-file limit
      if (current.length >= 10) return;
      set({ referencedFiles: [...current, filePath] });
    },

    removeFileReference: (filePath: string) => {
      set({ referencedFiles: get().referencedFiles.filter((f: string) => f !== filePath) });
    },

    setIsSubmitting: (isSubmitting) => set({ isSubmitting }),

    isLengthExceeded: () =>
      get().prompt !== null && get().prompt!.length > get().maxPromptLength,

    saveInLocalStorage: () => {
      if (get().prompt !== null && get().prompt !== "") {
        localStorage.setItem("prompt", get().prompt!);
      }

      const attachedImage = get().attachedImage;
      if (attachedImage) {
        const reader = new FileReader();
        reader.onload = () => {
          const base64String = reader.result as string;
          localStorage.setItem("attachedImage", base64String);
          localStorage.setItem("attachedImageName", attachedImage.name);
          localStorage.setItem("attachedImageType", attachedImage.type);
        };
        reader.readAsDataURL(attachedImage);
      }
    },

    loadFromLocalStorage: async () => {
      const savedPrompt = localStorage.getItem("prompt");
      let attachedImage: File | null = null;

      const savedImageBase64 = localStorage.getItem("attachedImage");
      const savedImageName = localStorage.getItem("attachedImageName");
      const savedImageType = localStorage.getItem("attachedImageType");
      if (savedImageBase64 && savedImageName && savedImageType) {
        const res = await fetch(savedImageBase64);
        const blob = await res.blob();
        attachedImage = new File([blob], savedImageName, {
          type: savedImageType,
        });
      }

      set({
        prompt: savedPrompt ?? get().prompt,
        attachedImage: attachedImage ?? get().attachedImage,
      });

      localStorage.removeItem("prompt");
      localStorage.removeItem("attachedImage");
      localStorage.removeItem("attachedImageName");
      localStorage.removeItem("attachedImageType");
    },

    clear: () => set({ prompt: null, attachedImage: null, attachedDesign: null, referencedFiles: [] }),
  },
}));

export const useMaxPromptLength = () =>
  usePromptInputStore((state) => state.maxPromptLength);
export const usePrompt = () => usePromptInputStore((state) => state.prompt);
export const useAttachedImage = () =>
  usePromptInputStore((state) => state.attachedImage);
export const useAttachedDesign = () =>
  usePromptInputStore((state) => state.attachedDesign);
export const useReferencedFiles = () =>
  usePromptInputStore((state) => state.referencedFiles);
export const useIsPromptSubmitting = () =>
  usePromptInputStore((state) => state.isSubmitting);
export const usePromptInputActions = () =>
  usePromptInputStore((state) => state.actions);

// Export the store for direct access from other stores (e.g., useModelStore)
export { usePromptInputStore };
